import { useEffect, useMemo, useState } from 'react';
import { fetchEntity, fetchTask } from '../api/client.js';
import type { WorkItemSummary } from '../types/workflow.js';
import { projectField } from '../lib/flowchart.js';
import {
  buildReviewerEnvelope,
  groupEnvelopeFields,
  isPureEditsEnvelope,
  readLiveField,
  type FieldDraft,
} from '../lib/editsEnvelopeEdit.js';
import { describeLocator } from './PatchPreview.js';

export function EditsEnvelopeEditor({ item, disabled, onSubmit, onDiscard }: {
  item: WorkItemSummary;
  disabled: boolean;
  onSubmit: (patch: Record<string, unknown>) => void;
  onDiscard: () => void;
}) {
  const [attempt, setAttempt] = useState(0);
  const [load, setLoad] = useState<{ state: 'loading' | 'failed' } | { state: 'loaded'; doc: unknown }>({ state: 'loading' });
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const edits = isPureEditsEnvelope(item.suggestedPatch) ? item.suggestedPatch._edits : [];
  const groups = useMemo(() => groupEnvelopeFields(edits), [item.suggestedPatch]);

  useEffect(() => {
    let active = true;
    setLoad({ state: 'loading' });
    setDraftValues({});
    const id = Number(item.targetId);
    const request = item.targetType === 'task' ? fetchTask(item.courseKey, id) : fetchEntity(item.courseKey, id);
    void request.then((doc) => { if (active) setLoad({ state: 'loaded', doc }); })
      .catch(() => { if (active) setLoad({ state: 'failed' }); });
    return () => { active = false; };
  }, [item.courseKey, item.targetId, item.targetType, attempt]);

  const prepared = useMemo(() => {
    if (load.state !== 'loaded') return null;
    const drafts: Record<string, FieldDraft> = {};
    for (const group of groups) {
      const label = describeLocator(group.locator);
      const live = readLiveField(load.doc, group.locator);
      if (live.state !== 'found') return { error: { label, reason: live.reason } };
      const projected = projectField(live.value, group.entries);
      if (projected.state === 'unavailable') return { error: { label, reason: projected.reason } };
      drafts[group.key] = {
        live: live.value,
        projected: projected.value,
        draft: draftValues[group.key] ?? projected.value,
      };
    }
    return { drafts };
  }, [load, groups, draftValues]);

  const result = prepared?.drafts ? buildReviewerEnvelope(edits, prepared.drafts) : null;
  const discard = <button className="rounded border border-slate-200 px-3 py-2 text-xs font-bold text-slate-500" onClick={onDiscard} type="button">Discard</button>;
  if (load.state === 'loading') return <div className="rounded-lg border border-blue-200 p-3 text-sm">Loading the live text…</div>;
  if (load.state === 'failed') return (
    <div className="flex flex-col gap-2 rounded-lg border border-blue-200 p-3 text-sm">
      <p>The live text could not be loaded, so this correction cannot be edited here. Try again, or reject it with your wording as a comment.</p>
      <div className="flex gap-2"><button onClick={() => setAttempt((value) => value + 1)} type="button">Try again</button>{discard}</div>
    </div>
  );
  if (prepared?.error) return (
    <div className="flex flex-col gap-2 rounded-lg border border-blue-200 p-3 text-sm">
      <p>This correction no longer matches the live text of {prepared.error.label} ({prepared.error.reason}), so it cannot be released as written. Reject it with your wording as a comment and it will be redrafted.</p>
      {discard}
    </div>
  );
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3" data-testid="edits-envelope-editor">
      <h3 className="text-sm font-bold text-slate-800">Edit the corrected text, then approve</h3>
      <p className="text-xs text-slate-600">Only fields you change are replaced as a whole; the others keep the reviewed correction.</p>
      {groups.map((group) => {
        const field = prepared?.drafts?.[group.key];
        if (!field) return null;
        const fieldLabel = describeLocator(group.locator);
        return <div className="flex flex-col gap-1" key={group.key}>
          <label className="text-xs font-bold text-slate-700" htmlFor={`edit-field-${item.workItemId}-${group.key}`}>{fieldLabel}</label>
          <textarea
            aria-label={`New text for ${fieldLabel}`}
            className="w-full rounded border border-slate-300 p-2 font-mono text-xs"
            id={`edit-field-${item.workItemId}-${group.key}`}
            onChange={(event) => setDraftValues((values) => ({ ...values, [group.key]: event.target.value }))}
            rows={Math.max(4, Math.min(16, field.draft.split('\n').length + Math.ceil(field.draft.length / 90)))}
            value={field.draft}
          />
          <details className="text-xs text-slate-600"><summary>Text learners see now</summary><p className="whitespace-pre-wrap">{field.live}</p></details>
        </div>;
      })}
      <p aria-live="polite" className="text-xs text-slate-600">{result && !result.ok ? result.message : ''}</p>
      <div className="flex gap-2">
        <button className="flex-1 rounded bg-primary py-2 text-xs font-bold text-white disabled:opacity-50" disabled={disabled || !result?.ok} onClick={() => { if (result?.ok) onSubmit(result.patch); }} type="button">Save &amp; approve</button>
        {discard}
      </div>
    </div>
  );
}
